import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";

/**
 * Score CobanQ — 0 a 1000. Faixas oficiais em memoria/project_score_cobanq.md.
 * 0 = sem historico (cinza), NAO ruim.
 * Enquanto nao ha backend real, `mockScore(cpf)` gera um score deterministico
 * pelo hash do CPF — mesma pessoa sempre com a mesma nota entre reloads.
 */

export interface ScoreSegment {
  from: number;
  to: number;
  color: string;
  label: string;
}

/**
 * Score Cobanq V3 (2026-09-19) — modelo aditivo baseado em diarias.
 * Cliente sem historico nasce em 0 ("Sem historico"). Faixas:
 *   0        Sem historico       (cinza)  <- default cliente novo
 *   1-299    Historico insuficiente (vermelho)
 *   300-599  Em construcao       (laranja) — risco medio
 *   600-799  Bom pagador         (verde claro)
 *   800-1000 Excelente pagador   (verde escuro) — Premium
 *
 * O segmento "Sem historico" cobre 0-1 (score exatamente 0) pra
 * diferenciar visualmente de um mau-pagador com score 1.
 */
export const SCORE_SEGMENTS: ScoreSegment[] = [
  { from: 0, to: 1, color: "#9E9E9E", label: "Sem histórico" },
  { from: 1, to: 300, color: "#E53935", label: "Histórico insuficiente" },
  { from: 300, to: 600, color: "#FB8C00", label: "Em construção" },
  { from: 600, to: 800, color: "#43A047", label: "Bom pagador" },
  { from: 800, to: 1001, color: "#1B5E20", label: "Excelente pagador" },
];

export function classifyScore(score: number): { color: string; label: string } {
  const seg =
    SCORE_SEGMENTS.find((s) => score >= s.from && score < s.to) ??
    SCORE_SEGMENTS[SCORE_SEGMENTS.length - 1];
  return { color: seg.color, label: seg.label };
}

export function mockScore(cpf?: string | null): number {
  const digits = (cpf || "").replace(/\D/g, "");
  if (!digits) return 0;
  let h = 0;
  for (let i = 0; i < digits.length; i++) h = (h * 31 + digits.charCodeAt(i)) >>> 0;
  const bucket = h % 100;
  if (bucket < 15) return 0;
  if (bucket < 35) return 50 + (h % 250);
  if (bucket < 65) return 300 + (h % 300);
  if (bucket < 90) return 600 + (h % 200);
  return 800 + (h % 201);
}

export interface ScoreDetails {
  pontualidade: number | null;
  operacoes: number;
  parcelasPagas: number;
  parcelasAtraso: number;
  maiorAtraso: number;
  renegociacoes: number;
  liquidadas: number;
  empresas: number;
}

export function mockScoreDetails(cpf?: string | null): ScoreDetails {
  const digits = (cpf || "").replace(/\D/g, "");
  const score = mockScore(cpf);
  if (score === 0) {
    return {
      pontualidade: null,
      operacoes: 0,
      parcelasPagas: 0,
      parcelasAtraso: 0,
      maiorAtraso: 0,
      renegociacoes: 0,
      liquidadas: 0,
      empresas: 0,
    };
  }
  let h = 0;
  for (let i = 0; i < digits.length; i++) h = (h * 17 + digits.charCodeAt(i)) >>> 0;
  const operacoes = 1 + (h % 15);
  const parcelasPagas = 5 + (h % 150);
  const parcelasAtraso = h % Math.max(1, Math.floor(parcelasPagas * 0.1));
  const totalParcelas = parcelasPagas + parcelasAtraso;
  const pontualidade =
    totalParcelas > 0 ? Math.round((parcelasPagas / totalParcelas) * 1000) / 10 : 100;
  return {
    pontualidade,
    operacoes,
    parcelasPagas,
    parcelasAtraso,
    maiorAtraso: h % 5,
    renegociacoes: h % 3,
    liquidadas: Math.max(0, operacoes - (h % 2)),
    empresas: 1 + (h % 4),
  };
}

/**
 * Circulo pequeno colorido com o valor do score dentro. Usado em listas
 * e tabelas. `size` controla o diametro em pixels.
 */
export interface ScoreBadgeProps {
  cpf?: string | null;
  score?: number;
  size?: number;
  onClick?: () => void;
  title?: string;
}

export function ScoreBadge({ cpf, score, size = 36, onClick, title = "Ver score" }: ScoreBadgeProps) {
  const value = score ?? mockScore(cpf);
  const { color } = classifyScore(value);
  const style = { backgroundColor: color, width: size, height: size, fontSize: size * 0.32 };
  if (!onClick) {
    return (
      <div
        title={title}
        className="flex items-center justify-center rounded-full font-bold text-white shadow-sm"
        style={style}
      >
        {value}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex items-center justify-center rounded-full font-bold text-white shadow-sm transition-transform hover:scale-105"
      style={style}
    >
      {value}
    </button>
  );
}

/**
 * Gauge semicircular 0-1000 igual ao ScoreGauge do mobile: track cinza,
 * arco colorido dividido em 5 faixas, agulha no valor atual, numero grande
 * embaixo com a classificacao textual.
 */
export interface ScoreGaugeProps {
  score: number;
  size?: number;
}

export function ScoreGauge({ score, size = 260 }: ScoreGaugeProps) {
  const width = size;
  const height = size * 0.7;
  const cx = width / 2;
  const cy = size * 0.62;
  const radius = size * 0.42;
  const strokeWidth = size * 0.08;
  const clamped = Math.min(Math.max(score, 0), 1000);
  const { color, label } = classifyScore(clamped);

  const polar = (angleDeg: number) => {
    const rad = ((angleDeg - 180) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };
  const arc = (startAngle: number, endAngle: number) => {
    const start = polar(startAngle);
    const end = polar(endAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  const segments = SCORE_SEGMENTS.map((seg) => ({
    color: seg.color,
    d: arc((seg.from / 1000) * 180, (seg.to / 1000) * 180),
  }));

  const needleAngle = (clamped / 1000) * 180;
  const needleRad = ((needleAngle - 180) * Math.PI) / 180;
  const needleR = radius - strokeWidth / 2;
  const needleEnd = {
    x: cx + needleR * Math.cos(needleRad),
    y: cy + needleR * Math.sin(needleRad),
  };

  return (
    <div className="flex flex-col items-center" style={{ width }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <path
          d={arc(0, 180)}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
        />
        {segments.map((s, i) => (
          <path
            key={i}
            d={s.d}
            stroke={s.color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
            fill="none"
          />
        ))}
        <path
          d={`M ${cx} ${cy} L ${needleEnd.x} ${needleEnd.y}`}
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={6} fill="currentColor" />
        <circle cx={cx} cy={cy} r={3} fill="white" />
      </svg>
      <div className="-mt-2 flex flex-col items-center">
        <span className="text-4xl font-extrabold leading-none" style={{ color }}>
          {clamped}
        </span>
        <span className="text-base font-semibold" style={{ color }}>
          {label}
        </span>
      </div>
    </div>
  );
}

/**
 * Gauge compacto pra tabela: mostra so a curva semicircular + numero
 * grande dentro. Usado na coluna SCORE de listas.
 */
export interface ScoreMiniGaugeProps {
  score: number;
  width?: number;
}

export function ScoreMiniGauge({ score, width = 80 }: ScoreMiniGaugeProps) {
  const height = width * 0.62;
  const clamped = Math.min(Math.max(score, 0), 1000);
  const { color, label } = classifyScore(clamped);
  const cx = width / 2;
  const cy = width * 0.55;
  const radius = width * 0.42;
  const strokeWidth = width * 0.09;

  const polar = (angleDeg: number) => {
    const rad = ((angleDeg - 180) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };
  const arc = (startAngle: number, endAngle: number) => {
    const start = polar(startAngle);
    const end = polar(endAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  const fillAngle = (clamped / 1000) * 180;

  return (
    <div className="flex flex-col items-center" style={{ width }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <path
          d={arc(0, 180)}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
        />
        {clamped > 0 && (
          <path
            d={arc(0, fillAngle)}
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
          />
        )}
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          fontSize={width * 0.24}
          fontWeight="700"
          fill={color}
        >
          {clamped}
        </text>
      </svg>
      <span
        className="mt-0.5 block w-full break-words text-center text-[10px] font-semibold uppercase leading-tight tracking-wide"
        style={{ color }}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * Modal de detalhes do score: exibe o ScoreGauge grande + pontualidade +
 * bloco de historico na rede CobanQ. Recebe `labels` pra permitir i18n
 * sem acoplar o @payflow/ui ao react-i18next.
 */
export interface ScoreDetailsDialogLabels {
  title: string;
  punctualityTitle: string;
  punctualityStats: (paid: number, total: number) => string;
  historyTitle: string;
  historyEmpty: string;
  historyLoans: string;
  historyPaidOff: string;
  historyPaidInstallments: string;
  historyOverdueInstallments: string;
  historyBiggestDelay: string;
  historyBiggestDelayValue: (days: number) => string;
  historyRenegotiations: string;
  historyPartners: string;
  disclaimer: string;
}

/**
 * Historico real vindo do backend (GET /clients/:id/score). Espelha
 * ScoreHistorico do service — nomes em pt-br, contagens reais.
 */
export interface ScoreRealData {
  score: number;
  pontualidade: number | null;
  historico: {
    operacoes: number;
    diariasPagas: number;
    diariasAtraso: number;
    maiorAtraso: number;
    renegociacoes: number;
    liquidadas: number;
    empresas: number;
  };
}

/**
 * Legenda das faixas do score. Realca o segmento em que o score atual
 * caiu com um "•" de bullet + texto em bold, e as outras faixas ficam
 * em cinza claro. Layout compacto pra caber no dialog sem empurrar o
 * resto pra baixo.
 */
export function ScoreLegend({ score }: { score: number }) {
  const activeIdx = SCORE_SEGMENTS.findIndex(
    (s) => score >= s.from && score < s.to,
  );
  return (
    <div className="w-full rounded-lg border border-border p-3">
      <div className="mb-2 text-xs uppercase text-muted-foreground">
        Níveis do score
      </div>
      <ul className="space-y-1.5">
        {SCORE_SEGMENTS.map((seg, idx) => {
          const isActive = idx === activeIdx;
          const range =
            seg.from === 0 && seg.to === 1
              ? "0"
              : seg.to === 1001
                ? `${seg.from}–1000`
                : `${seg.from}–${seg.to - 1}`;
          return (
            <li
              key={seg.label}
              className={
                "flex items-center gap-2 text-xs " +
                (isActive ? "font-bold text-foreground" : "text-muted-foreground")
              }
            >
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{
                  background: seg.color,
                  outline: isActive ? "2px solid rgba(0,0,0,0.15)" : "none",
                  outlineOffset: 1,
                }}
              />
              <span className="w-16 tabular-nums">{range}</span>
              <span>{seg.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export interface ScoreDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cpf?: string | null;
  /**
   * Se preenchido, usa os dados reais em vez do mock derivado do CPF.
   * Em `loading` mostra placeholder — mockScore(cpf) fica so como fallback
   * caso o fetch nao chegue por qualquer motivo.
   */
  data?: ScoreRealData | null;
  loading?: boolean;
  headerSubtitle?: string;
  labels: ScoreDetailsDialogLabels;
}

export function ScoreDetailsDialog({
  open,
  onOpenChange,
  cpf,
  data,
  loading,
  headerSubtitle,
  labels,
}: ScoreDetailsDialogProps) {
  const score = data?.score ?? mockScore(cpf);
  const details = data
    ? {
        pontualidade: data.pontualidade,
        operacoes: data.historico.operacoes,
        parcelasPagas: data.historico.diariasPagas,
        parcelasAtraso: data.historico.diariasAtraso,
        maiorAtraso: data.historico.maiorAtraso,
        renegociacoes: data.historico.renegociacoes,
        liquidadas: data.historico.liquidadas,
        empresas: data.historico.empresas,
      }
    : mockScoreDetails(cpf);
  const totalVencidas = details.parcelasPagas + details.parcelasAtraso;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          {headerSubtitle && (
            <div className="text-sm text-muted-foreground text-center">{headerSubtitle}</div>
          )}
          <div className={loading ? "opacity-40 transition-opacity" : "transition-opacity"}>
            <ScoreGauge score={score} />
          </div>

          {/* Legenda de faixas — mostra ao operador os niveis do score
              e destaca em qual o cliente atual esta. Ajuda a interpretar
              a nota sem precisar decorar os limites. */}
          <ScoreLegend score={score} />

          {details.pontualidade !== null && (
            <div className="w-full rounded-lg border border-border p-3">
              <div className="text-xs uppercase text-muted-foreground">
                {labels.punctualityTitle}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-green-600">
                  {details.pontualidade.toFixed(1)}%
                </span>
                <span className="text-xs text-muted-foreground">
                  {labels.punctualityStats(details.parcelasPagas, totalVencidas)}
                </span>
              </div>
            </div>
          )}

          <div className="w-full rounded-lg border border-border p-3">
            <div className="mb-2 text-xs uppercase text-muted-foreground">
              {labels.historyTitle}
            </div>
            {details.operacoes === 0 ? (
              <p className="text-sm text-muted-foreground">{labels.historyEmpty}</p>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.historyLoans}</span>
                  <span className="font-semibold">{details.operacoes}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.historyPaidOff}</span>
                  <span className="font-semibold">{details.liquidadas}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.historyPaidInstallments}</span>
                  <span className="font-semibold">{details.parcelasPagas}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.historyOverdueInstallments}</span>
                  <span className="font-semibold">{details.parcelasAtraso}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.historyBiggestDelay}</span>
                  <span className="font-semibold">
                    {labels.historyBiggestDelayValue(details.maiorAtraso)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.historyRenegotiations}</span>
                  <span className="font-semibold">{details.renegociacoes}</span>
                </div>
                <div className="col-span-2 flex justify-between border-t border-border pt-2">
                  <span className="text-muted-foreground">{labels.historyPartners}</span>
                  <span className="font-semibold">{details.empresas}</span>
                </div>
              </div>
            )}
          </div>

          <div className="text-[10px] text-muted-foreground italic text-center">
            {labels.disclaimer}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
